import { useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

import { cn } from "@/lib/utils";
import { urlDelArchivoDeTerreno, type AudioDeTerreno } from "@/lib/terreno";

import { duracionDelAudio } from "./registroDeTerreno";

/**
 * El audio que está sonando, en TODA la página: reproducir uno pausa el otro.
 * Dos notas de voz a la vez son ininteligibles, y el encargado manda varias
 * por sección.
 */
let sonando: HTMLAudioElement | null = null;

const BARRAS = 44;

/**
 * Las barras de la "onda": DETERMINISTAS a partir del uuid, para que la misma
 * nota se dibuje igual en cada render. No son la forma de onda real —eso
 * exigiría decodificar el audio—: son el indicador de avance del diseño.
 */
function alturasDeLasBarras(semilla: string): number[] {
  let estado = Array.from(semilla).reduce((acc, c) => (acc * 31 + c.charCodeAt(0)) % 233280, 7);

  return Array.from({ length: BARRAS }, () => {
    estado = (estado * 9301 + 49297) % 233280;

    return 22 + Math.round((estado / 233280) * 78);
  });
}

interface ReproductorDeAudioDeTerrenoProps {
  informeId: number;
  audio: AudioDeTerreno;
  /** La fila del diseño: botón de 26 px, onda de 18 y solo la duración total. */
  compacto?: boolean;
}

/**
 * Reproductor compacto de una nota de voz: botón redondo, onda de avance y
 * "m:ss / m:ss", sobre un `<audio preload="none">` real.
 *
 * `preload="none"` es lo que mantiene barata la pestaña: un informe trae
 * decenas de notas y ninguna debe descargarse hasta que alguien la pida.
 */
export function ReproductorDeAudioDeTerreno({
  informeId,
  audio,
  compacto = false,
}: ReproductorDeAudioDeTerrenoProps) {
  const elemento = useRef<HTMLAudioElement>(null);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [actual, setActual] = useState(0);
  const [duracionReal, setDuracionReal] = useState<number | null>(null);

  const duracion = duracionReal ?? (audio.duracion_ms ?? 0) / 1000;
  const avance = duracion > 0 ? Math.min(1, actual / duracion) : 0;
  const barras = alturasDeLasBarras(audio.uuid);

  if (!audio.archivo) {
    return <p className="text-ink-hint text-xs font-semibold">Audio no disponible.</p>;
  }

  const src = urlDelArchivoDeTerreno(informeId, audio.uuid, audio.archivo.hash);

  function alternar() {
    const el = elemento.current;
    if (!el) return;

    if (!el.paused) {
      el.pause();
      return;
    }

    if (sonando && sonando !== el) sonando.pause();
    sonando = el;
    void el.play();
  }

  function saltarA(fraccion: number) {
    const el = elemento.current;
    if (!el || duracion <= 0) return;

    el.currentTime = fraccion * duracion;
    setActual(el.currentTime);
  }

  return (
    <div className="flex items-center gap-2.5">
      <button
        type="button"
        onClick={alternar}
        aria-label={reproduciendo ? "Pausar" : "Reproducir"}
        className={cn(
          "bg-ink text-card focus-visible:ring-ring/50 flex flex-none items-center justify-center rounded-full outline-none focus-visible:ring-[3px]",
          compacto ? "size-6.5" : "size-8",
        )}
      >
        {reproduciendo ? (
          <Pause className="size-3" fill="currentColor" />
        ) : (
          <Play className="ml-0.5 size-3" fill="currentColor" />
        )}
      </button>

      {/* La onda también es un control: un clic salta a esa fracción. No es un
          `<progress>` porque se puede operar, y no un `<input type=range>`
          porque el diseño la dibuja como barras sueltas. */}
      <div
        role="presentation"
        onClick={(e) => {
          const caja = e.currentTarget.getBoundingClientRect();
          if (caja.width > 0) saltarA((e.clientX - caja.left) / caja.width);
        }}
        className={cn(
          "flex min-w-0 flex-1 cursor-pointer items-center gap-0.5",
          compacto ? "h-[1.125rem]" : "h-6",
        )}
      >
        {barras.map((alto, i) => (
          <span
            key={i}
            style={{ height: `${alto}%` }}
            className={cn(
              "flex-1 rounded-sm",
              compacto ? "max-w-[0.1875rem]" : "max-w-1",
              i / BARRAS < avance ? "bg-primary" : "bg-ink-ghost",
            )}
          />
        ))}
      </div>

      <span
        className={cn(
          "text-ink-body flex-none text-right font-mono font-bold",
          compacto ? "text-[0.65625rem]" : "min-w-[4.5rem] text-[0.6875rem]",
        )}
      >
        {!compacto && (reproduciendo || actual > 0)
          ? `${duracionDelAudio(actual * 1000)} / ${duracionDelAudio(duracion * 1000)}`
          : duracionDelAudio(duracion * 1000)}
      </span>

      <audio
        ref={elemento}
        src={src}
        preload="none"
        onPlay={() => setReproduciendo(true)}
        onPause={() => setReproduciendo(false)}
        onEnded={() => {
          setReproduciendo(false);
          setActual(0);
        }}
        onTimeUpdate={(e) => setActual(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDuracionReal(d);
        }}
      />
    </div>
  );
}
